import React, { useState, useEffect } from 'react';
import { TrendingUp, AlertTriangle, RefreshCw } from 'lucide-react';
import { getOrgAnalytics, getPlatformAnalytics } from '../../api/billingApi';

/**
 * MarginModel — admin P&L view.
 * Shows per-org revenue vs OR cost, gross margin.
 *
 * Props:
 *   orgId   — string UUID (org-level view)
 *   isAdmin — boolean (show platform-wide view if true)
 */
function MarginModel({ orgId, isAdmin }) {
  const [orgData, setOrgData] = useState(null);
  const [platformData, setPlatformData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [org, platform] = await Promise.all([
        orgId ? getOrgAnalytics({ orgId }) : null,
        isAdmin ? getPlatformAnalytics() : null,
      ]);
      setOrgData(org);
      setPlatformData(platform);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [orgId, isAdmin]); // eslint-disable-line

  const toGbp = (v) => v != null ? `£${parseFloat(v).toFixed(2)}` : '—';
  const toPct = (v) => v != null ? `${parseFloat(v).toFixed(1)}%` : '—';

  const marginPct = orgData?.estimated_margin_pct;
  const marginColor = marginPct == null
    ? 'text-gray-400'
    : parseFloat(marginPct) < 20
    ? 'text-red-600'
    : 'text-green-600';

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
          <TrendingUp className="w-4 h-4" /> Margin Model
        </h3>
        <button
          onClick={load}
          disabled={loading}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-30"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-600 mb-3">{error}</p>
      )}

      {/* Org-level P&L */}
      {orgData && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Revenue (credits charged)" value={`${parseFloat(orgData.total_credits_charged || 0).toFixed(0)} cr`} />
            <Stat label="Revenue (GBP)" value={toGbp(orgData.total_gbp_charged)} />
            <Stat label="Infra cost est." value={toGbp(orgData.estimated_infra_cost_gbp)} />
            <Stat
              label="Gross margin"
              value={toPct(orgData.estimated_margin_pct)}
              valueClass={marginColor}
            />
            <Stat label="Total runs" value={(orgData.total_runs || 0).toLocaleString()} />
            <Stat label="Total tokens" value={(orgData.total_tokens || 0).toLocaleString()} />
          </div>

          {/* Low-margin alert */}
          {marginPct != null && parseFloat(marginPct) < 20 && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              Gross margin below 20% target floor.
            </div>
          )}

          {/* Cost breakdown */}
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 font-medium mb-2">Cost breakdown (credits)</p>
            <div className="space-y-1">
              {[
                ['Model', orgData.total_model_credits],
                ['Runtime', orgData.total_runtime_credits],
                ['Tools', orgData.total_tool_credits],
                ['Storage', orgData.total_storage_credits],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between text-xs">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-medium text-gray-700">{parseFloat(val || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Platform-wide summary (admin only) */}
      {isAdmin && platformData && (
        <div className="mt-5 pt-5 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Platform-wide</p>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Active orgs" value={(platformData.total_orgs_active || 0).toLocaleString()} />
            <Stat label="Total revenue" value={toGbp(platformData.total_gbp_charged)} />
          </div>
          {platformData.by_tier && Object.keys(platformData.by_tier).length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-gray-400 mb-2">By tier</p>
              {Object.entries(platformData.by_tier).map(([tier, stats]) => (
                <div key={tier} className="flex justify-between text-xs py-0.5">
                  <span className="text-gray-500 capitalize">{tier}</span>
                  <span className="text-gray-700">{stats.orgs} orgs · £{parseFloat(stats.total_gbp || 0).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!orgData && !loading && (
        <p className="text-sm text-gray-400 text-center py-4">No data</p>
      )}
    </div>
  );
}

function Stat({ label, value, valueClass = 'text-gray-900' }) {
  return (
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className={`text-sm font-semibold ${valueClass}`}>{value ?? '—'}</p>
    </div>
  );
}

export default MarginModel;
